import React, {useEffect, useMemo, useState} from 'react';
import Translate from "../../../../../../../../components/Translate";
import {
    addWeekdayWorkingHours,
    enableOrDisableWorkingHours,
    getWorkingHoursTimezones,
    removeWeekdayWorkingHours,
    setNonWorkingDays,
    setWeekdayWorkingHours,
    setWorkingHoursTimezone,
    updateCompanyWorkingHoursMoveToUnmanagedList
} from "../../../../../../../../services/company/api";
import TimeInput from "../../../../../../../../components-new/form/TimeInput";
import Switch from "../../../../../../../../components-new/form/Switch";
import colors from "../../../../../../../../utils/Colors";
import SettingCard from "../../../../../../../../components-new/ui/SettingCard";
import MultiDatePicker from "../../../../../../../../components-new/form/DatePicker/MultiDatePicker";
import {toLocalDateString} from "../../../../../../../../utils/DateUtils";
import config from "../../../../../../../../config";
import SelectableCard from "../../../../../../../../components-new/general/SelectableCard";
import Checkbox from "../../../../../../../../components-new/form/Checkbox";
import DropdownSelect from "../../../../../../../../components-new/form/DropdownSelect";

const isValidTime = (value) => /^([01]\d|2[0-3]):([0-5]\d)$/.test(value);

const resolveTimezone = (timezone) => {
    try {
        return new Intl.DateTimeFormat('en', timezone ? {timeZone: timezone} : {}).resolvedOptions().timeZone;
    } catch (e) {
        return null;
    }
};

const getTimezoneOffset = (timezone, date) => {
    try {
        const parts = new Intl.DateTimeFormat('en', {
            timeZone: timezone,
            timeZoneName: 'longOffset'
        }).formatToParts(date);
        const offset = parts.find(part => part.type === 'timeZoneName').value;
        return offset === 'GMT' ? 'UTC+00:00' : offset.replace('GMT', 'UTC');
    } catch (e) {
        return '';
    }
};

const WorkingHoursSettings = ({client, workingHours, setWorkingHours, isFetching, addNotification}) => {
    const [timezones, setTimezones] = useState([]);
    const [isSavingTimezone, setIsSavingTimezone] = useState(false);
    const [isSavingEnabled, setIsSavingEnabled] = useState(false);
    const [timezoneError, setTimezoneError] = useState(null);
    const userTimezone = useMemo(() => resolveTimezone(), []);
    const defaultTimezone = useMemo(() => {
        if (!userTimezone) return 'UTC';
        return timezones.find(zone => zone === userTimezone)
            || timezones.find(zone => resolveTimezone(zone) === userTimezone)
            || 'UTC';
    }, [timezones, userTimezone]);
    const selectedTimezone = workingHours.timezone || defaultTimezone;
    const timezoneOptions = useMemo(() => {
        const now = new Date();
        return Array.from(new Set(['UTC', selectedTimezone, ...timezones])).map(value => ({
            value,
            offset: getTimezoneOffset(value, now),
            isUserTimezone: !!userTimezone && resolveTimezone(value) === userTimezone
        }));
    }, [timezones, selectedTimezone, userTimezone]);

    useEffect(() => {
        let active = true;
        getWorkingHoursTimezones().then(data => {
            if (active) setTimezones(data);
        }).catch(() => {
            if (active) setTimezoneError('workingHoursTimezonesLoadError');
        });
        return () => { active = false; };
    }, []);

    const handleTimezoneChange = (option) => {
        setIsSavingTimezone(true);
        setTimezoneError(null);
        setWorkingHoursTimezone(client.id, option.value).then(data => {
            setWorkingHours(prev => ({...prev, timezone: data.timezone}));
            addNotification('workingHoursTimezoneSaveSuccess', config.notificationLevels.success);
        }).catch(() => {
            addNotification('workingHoursTimezoneSaveError', config.notificationLevels.error);
        }).finally(() => setIsSavingTimezone(false));
    };

    const renderTimezone = option => (
        <React.Fragment>
            {option.value}{option.offset && ` (${option.offset})`}{option.isUserTimezone && <React.Fragment> (<Translate text="yourTimezone"/>)</React.Fragment>}
        </React.Fragment>
    );

    const handleUnmanagedCheckboxChange = (e) => {
        updateCompanyWorkingHoursMoveToUnmanagedList(client.id, e)
        setWorkingHours((prev) => ({
            ...prev,
            moveToUnmanagedList: e
        }));
    }

    const handleEnabledCheckboxChange = (e) => {
        setIsSavingEnabled(true);
        enableOrDisableWorkingHours(client.id, e, selectedTimezone).then(data => {
            setWorkingHours(prev => ({...prev, enabled: data.enabled, timezone: data.timezone}));
        }).catch(() => {
            addNotification('somethingWentWrong', config.notificationLevels.error);
        }).finally(() => setIsSavingEnabled(false));
    }

    const handleNonWorkingDaysChange = (e) => {
        setNonWorkingDays(client.id, e.map(d => toLocalDateString(d)))
        setWorkingHours((prev) => ({
            ...prev,
            nonWorkingDays: e
        }))
    }

    const handleWeekDayChange = (e) => {
        if (/^\d{0,2}:?\d{0,2}$/.test(e.start) && /^\d{0,2}:?\d{0,2}$/.test(e.end)) {
            setWorkingHours((prev) => ({
                ...prev,
                weekdayHours: {
                    ...prev.weekdayHours,
                    [e.dayOfWeek]: {
                        start: e.start,
                        end: e.end,
                        fullDay: e.fullDay,
                        active: true
                    }
                }
            }))

            if (isValidTime(e.start) && isValidTime(e.end)) {
                setWeekdayWorkingHours(client.id, e.dayOfWeek, e.start, e.end, e.fullDay)
            }
        }
    }

    const handleWeekDaySelect = (day, value) => {
        if (value) {
            addWeekdayWorkingHours(client.id, day).then(data => {
                setWorkingHours((prev) => ({
                    ...prev,
                    weekdayHours: data.weekdayHours
                }))
            })
        } else {
            removeWeekdayWorkingHours(client.id, day).then(data => {
                setWorkingHours((prev) => ({
                    ...prev,
                    weekdayHours: data.weekdayHours
                }))
            })
        }

    }

    return (
        <SettingCard
            titleKey="workingHours"
            toggle={
                <Switch
                    checked={workingHours.enabled}
                    disabled={isFetching || isSavingEnabled || isSavingTimezone || (!workingHours.timezone && !timezones.length && !timezoneError)}
                    onChange={handleEnabledCheckboxChange}
                    style={{color: colors.silverBlue}}
                />
            }
        >
            {workingHours &&
                <div className={'setting-stack' + (!workingHours.enabled ? ' setting-body--off' : '')}>

                    <div className="setting-field">
                        <span className="setting-label"><Translate text="timezone"/></span>
                        <DropdownSelect
                            styles={{width: '480px', maxWidth: '100%'}}
                            options={timezoneOptions}
                            selectedValue={timezoneOptions.find(option => option.value === selectedTimezone)}
                            onChange={handleTimezoneChange}
                            renderLabel={renderTimezone}
                            searchBy={(option, search) => `${option.value} ${option.offset}`.toLowerCase().includes(search.toLowerCase())}
                            disabled={isFetching || isSavingEnabled || isSavingTimezone || !workingHours.enabled || !timezones.length}
                            overlay
                        >
                            {renderTimezone}
                        </DropdownSelect>
                        {timezoneError && <span role="alert"><Translate text={timezoneError}/></span>}
                    </div>

                    <p className="setting-hint"><Translate text="weekDayConfigDescription"/></p>

                    <div className="setting-days">
                        {
                            Object.values(config.weekdays).map((day) =>
                                <SelectableCard
                                    style={{
                                        minWidth: '150px',
                                        flexShrink: 0
                                    }}
                                    key={day}
                                    isSelected={workingHours.weekdayHours[day] && workingHours.weekdayHours[day].active}
                                    onSelect={() =>
                                        handleWeekDaySelect(day, !(workingHours.weekdayHours[day] && workingHours.weekdayHours[day].active))
                                    }
                                >
                                    <div style={{
            display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: '5px'
                                    }}>
                                          <span style={{
                                              display: 'inline-block',
                                              fontWeight: 'bold',
                                              marginBottom: '10px',
                                          }}>
                                           <Translate text={day}/>
                                        </span>

                                        <Checkbox
                                            style={{
                                                marginBottom: '10px',
                                            }}
                                            checked={workingHours.weekdayHours[day] && workingHours.weekdayHours[day].active}
                                            handleChange={() =>
                                                handleWeekDaySelect(day, !(workingHours.weekdayHours[day] && workingHours.weekdayHours[day].active))
                                            }
                                            onClick={e => e.stopPropagation()}
                                        />
                                    </div>

                                    <TimeInput
                                        label="startHour"
                                        name="startHour"
                                        value={workingHours.weekdayHours[day] ? workingHours.weekdayHours[day].start : "00:00"}
                                        onChange={(e) => handleWeekDayChange({
                                            dayOfWeek: day,
                                            start: e.target.value,
                                            end: workingHours.weekdayHours[day] ? workingHours.weekdayHours[day].end : "00:00",
                                            fullDay: workingHours.weekdayHours[day] ? workingHours.weekdayHours[day].fullDay : false
                                        })}
                                        disabled={!(workingHours.weekdayHours[day] && workingHours.weekdayHours[day].active && !workingHours.weekdayHours[day].fullDay)}
                                    />

                                    <TimeInput
                                        label="endHour"
                                        name="endHour"
                                        value={workingHours.weekdayHours[day] ? workingHours.weekdayHours[day].end : "00:00"}
                                        onChange={(e) => handleWeekDayChange({
                                            dayOfWeek: day,
                                            end: e.target.value,
                                            start: workingHours.weekdayHours[day] ? workingHours.weekdayHours[day].start : "00:00",
                                            fullDay: workingHours.weekdayHours[day] ? workingHours.weekdayHours[day].fullDay : false
                                        })}
                                        disabled={!(workingHours.weekdayHours[day] && workingHours.weekdayHours[day].active && !workingHours.weekdayHours[day].fullDay)}
                                    />

                                    <Checkbox
                                        checked={workingHours.weekdayHours[day] ? workingHours.weekdayHours[day].fullDay : false}
                                        handleChange={(e) => handleWeekDayChange({
                                            dayOfWeek: day,
                                            end: workingHours.weekdayHours[day] ? workingHours.weekdayHours[day].end : "00:00",
                                            start: workingHours.weekdayHours[day] ? workingHours.weekdayHours[day].start : "00:00",
                                            fullDay: e
                                        })}
                                        label={<Translate text="fullDay"/>}
                                        onClick={e => e.stopPropagation()}
                                        disabled={!(workingHours.weekdayHours[day] && workingHours.weekdayHours[day].active)}
                                    />
                                </SelectableCard>
                            )
                        }
                    </div>

                    <div className="setting-field">
                        <span className="setting-label"><Translate text={'nonWorkingDays'}/></span>

                        <MultiDatePicker
                            value={workingHours.nonWorkingDays.map(d => new Date(d))}
                            onChange={handleNonWorkingDaysChange}
                            popupStyle={{width: '100%'}}
                            popupDirection='top'
                            onReset={() => handleNonWorkingDaysChange([])}
                        />
                    </div>

                    <Switch
                        checked={workingHours.moveToUnmanagedList}
                        onChange={handleUnmanagedCheckboxChange}
                        label={<Translate text="moveToUnmanagedListOnRequestToOperator"/>}
                        style={{color: colors.silverBlue}}
                        disabled={!workingHours.enabled}
                    />

                </div>
            }
        </SettingCard>
    );
};

export default WorkingHoursSettings;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/management/clients/new/functions/components/WorkingHoursSettings.jsx